import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, MapPin, Clock, Shield, Users, TrendingUp, Mail, Star, Send, Edit2, Trash2, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [myReview, setMyReview] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review_text: '' });
  const [isEditingReview, setIsEditingReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Fetch reviews on mount
  useEffect(() => {
    fetchReviews();
    fetchReviewStats();
  }, []);

  // Fetch user's review if logged in
  useEffect(() => {
    if (user && token) {
      fetchMyReview();
    }
  }, [user, token]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const fetchReviewStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setReviewStats(response.data);
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  };

  const fetchMyReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setMyReview(response.data);
        setReviewForm({ rating: response.data.rating, review_text: response.data.review_text });
      }
    } catch (error) {
      console.error('Failed to fetch my review:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to leave a review');
      navigate('/auth');
      return;
    }

    if (reviewForm.review_text.length < 10) {
      toast.error('Review must be at least 10 characters');
      return;
    }

    setReviewLoading(true);
    try {
      if (isEditingReview) {
        await axios.put(`${API}/reviews`, reviewForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review updated successfully!');
      } else {
        await axios.post(`${API}/reviews`, reviewForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Thank you for your review!');
      }
      setShowReviewForm(false);
      setIsEditingReview(false);
      fetchReviews();
      fetchReviewStats();
      fetchMyReview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!window.confirm('Are you sure you want to delete your review?')) return;
    
    try {
      await axios.delete(`${API}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      setMyReview(null);
      setReviewForm({ rating: 5, review_text: '' });
      fetchReviews();
      fetchReviewStats();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleEditReview = () => {
    setIsEditingReview(true);
    setShowReviewForm(true);
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            disabled={!interactive}
          >
            <Star
              className={`w-5 h-5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}`}
            />
          </button>
        ))}
      </div>
    );
  };


  // Handle logo click - go to dashboard if logged in, otherwise stay on landing
  const handleLogoClick = () => {
    if (user) {
      navigate(user.role === 'driver' ? '/driver' : user.role === 'admin' ? '/admin' : '/rider');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1696798637345-6e58432ca556?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNpdHklMjBzdHJlZXQlMjBuaWdodHxlbnwwfHx8fDE3NjkxNDYxNDR8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/90" />
        </div>

        {/* Navbar */}
        <nav className="relative z-10 px-6 py-6 flex items-center justify-between max-w-7xl mx-auto">
          <button 
            onClick={handleLogoClick}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="logo-home-btn"
          >
            <img src="/logo.png" alt="vgoride" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-outfit font-bold">vgoride</span>
          </button>
          <div className="flex items-center gap-4">
            {user ? (
              <button
                onClick={() => navigate(user.role === 'driver' ? '/driver' : user.role === 'admin' ? '/admin' : '/rider')}
                className="bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full px-6 py-3 font-medium transition-all"
                data-testid="nav-dashboard-btn"
              >
                Go to Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/rides-available')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 rounded-full px-6 py-3 font-medium transition-all"
                  data-testid="nav-rides-btn"
                >
                  Rides Available
                </button>
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/10 rounded-full px-6 py-3 font-medium transition-all"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-outfit font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-6 leading-tight">
              Your Ride,
              <br />
              <span className="neon-text">Anytime, Anywhere</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-2xl">
              Experience the future of ride-hailing. Fast, reliable, and available 24/7. Whether you're heading to work or exploring the city, vgoride gets you there.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/auth?mode=register&role=rider')}
                className="bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold px-8 py-6 shadow-[0_0_20px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                data-testid="hero-rider-signup-btn"
              >
                Sign Up as Rider
              </button>
              <button
                onClick={() => navigate('/auth?mode=register&role=driver')}
                className="bg-white/10 text-white hover:bg-white/20 rounded-full font-medium px-8 py-6 backdrop-blur-md border border-white/10 transition-all"
                data-testid="hero-driver-signup-btn"
              >
                Become a Driver
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 px-6 bg-dark-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl mb-4">Why Choose vgoride?</h2>
            <p className="text-lg text-white/60">Built for the modern commuter</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: '24/7 Availability', desc: 'Rides whenever you need them, day or night' },
              { icon: Shield, title: 'Safe & Secure', desc: 'Verified drivers and real-time tracking' },
              { icon: MapPin, title: 'Live Tracking', desc: 'Track your driver in real-time on the map' },
              { icon: Users, title: 'Trusted Community', desc: 'Join thousands of satisfied riders' },
              { icon: TrendingUp, title: 'Fair Pricing', desc: 'Transparent fares with no hidden charges' },
              { icon: Car, title: 'Quality Rides', desc: 'Well-maintained vehicles and professional drivers' },
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-dark-surface border border-white/5 rounded-2xl p-8 hover:border-white/10 transition-colors"
                data-testid={`feature-${idx}`}
              >
                <div className="w-14 h-14 rounded-full bg-neon-cyan/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-neon-cyan" strokeWidth={2} />
                </div>
                <h3 className="font-outfit font-semibold text-xl mb-3">{feature.title}</h3>
                <p className="text-white/60">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="py-24 px-6 bg-dark-surface" id="reviews">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl mb-4">What Our Riders Say</h2>
            <p className="text-lg text-white/60 mb-6">Real reviews from the vgoride community</p>
            
            {/* Stats */}
            {reviewStats.total_reviews > 0 && (
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="flex items-center gap-2">
                  {renderStars(Math.round(reviewStats.average_rating))}
                  <span className="text-2xl font-bold text-yellow-400">{reviewStats.average_rating}</span>
                </div>
                <span className="text-white/40">|</span>
                <span className="text-white/60">{reviewStats.total_reviews} review{reviewStats.total_reviews !== 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Write Review Button */}
            {user ? (
              myReview ? (
                <div className="flex items-center justify-center gap-3">
                  <span className="text-white/60">You've already reviewed</span>
                  <button
                    onClick={handleEditReview}
                    className="flex items-center gap-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black px-4 py-2 rounded-full transition-all"
                    data-testid="edit-review-btn"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleDeleteReview}
                    className="flex items-center gap-2 bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 rounded-full transition-all"
                    data-testid="delete-review-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="flex items-center gap-2 bg-neon-cyan text-black hover:bg-[#00B8D4] px-6 py-3 rounded-full font-medium transition-all mx-auto"
                  data-testid="write-review-btn"
                >
                  <MessageSquare className="w-5 h-5" />
                  Write a Review
                </button>
              )
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 px-6 py-3 rounded-full font-medium transition-all mx-auto border border-white/10"
                data-testid="signin-to-review-btn"
              >
                <MessageSquare className="w-5 h-5" />
                Sign in to Write a Review
              </button>
            )}
          </div>

          {/* Review Form Modal */}
          {showReviewForm && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-dark-surface border border-white/10 rounded-2xl p-6 w-full max-w-md"
              >
                <h3 className="font-outfit font-bold text-2xl mb-6">
                  {isEditingReview ? 'Edit Your Review' : 'Share Your Experience'}
                </h3>
                <form onSubmit={handleSubmitReview} className="space-y-6">
                  <div>
                    <label className="block text-white/80 mb-3">Your Rating</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star
                            className={`w-8 h-8 ${star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/80 mb-3">Your Review</label>
                    <textarea
                      value={reviewForm.review_text}
                      onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                      placeholder="Tell us about your experience with vgoride..."
                      className="w-full bg-dark-bg border border-white/10 rounded-xl p-4 text-white placeholder:text-white/40 focus:border-neon-cyan focus:outline-none resize-none h-32"
                      maxLength={500}
                      data-testid="review-text-input"
                    />
                    <div className="text-right text-white/40 text-sm mt-1">
                      {reviewForm.review_text.length}/500
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReviewForm(false);
                        setIsEditingReview(false);
                      }}
                      className="flex-1 bg-white/10 text-white hover:bg-white/20 py-3 rounded-full font-medium transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reviewLoading || reviewForm.review_text.length < 10}
                      className="flex-1 bg-neon-cyan text-black hover:bg-[#00B8D4] py-3 rounded-full font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="submit-review-btn"
                    >
                      {reviewLoading ? (
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {isEditingReview ? 'Update Review' : 'Submit Review'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}

          {/* Reviews Grid */}
          {reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, idx) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-dark-bg border rounded-2xl p-6 ${
                    myReview && myReview.id === review.id ? 'border-neon-cyan' : 'border-white/5'
                  }`}
                  data-testid={`review-card-${idx}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center text-white font-bold">
                        {review.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-white">
                          {review.user_name}
                          {myReview && myReview.id === review.id && (
                            <span className="ml-2 text-xs text-neon-cyan">(You)</span>
                          )}
                        </div>
                        <div className="text-xs text-white/40">
                          {new Date(review.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    {renderStars(review.rating)}
                  </div>
                  <p className="text-white/80 leading-relaxed">{review.review_text}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <MessageSquare className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 text-lg">No reviews yet. Be the first to share your experience!</p>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-6 bg-dark-surface">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-outfit font-bold text-4xl sm:text-5xl mb-6">Ready to Ride?</h2>
          <p className="text-lg text-white/60 mb-10">
            Join vgoride today and experience hassle-free transportation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/auth?mode=register')}
              className="bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold px-10 py-6 shadow-[0_0_20px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
              data-testid="cta-signup-btn"
            >
              Get Started Now
            </button>
            <button
              onClick={() => navigate('/auth?mode=login')}
              className="bg-white/10 text-white hover:bg-white/20 rounded-full font-bold px-10 py-6 border border-white/20 transition-all hover:scale-105"
              data-testid="browse-rides-btn"
            >
              Browse Available Rides
            </button>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-12 px-6 bg-dark-surface border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="font-outfit font-bold text-2xl text-white mb-4">Need Help?</h3>
          <p className="text-white/60 mb-4">Our support team is here for you 24/7</p>
          <a 
            href="mailto:customer-support@vgoride.net" 
            className="inline-flex items-center gap-2 text-neon-cyan hover:text-white transition-colors text-lg"
            data-testid="contact-email-link"
          >
            <Mail className="w-5 h-5" />
            customer-support@vgoride.net
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-dark-bg border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">&copy; 2025 vgoride. All rights reserved.</p>
          <a 
            href="mailto:customer-support@vgoride.net" 
            className="text-white/40 hover:text-neon-cyan text-sm transition-colors flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            customer-support@vgoride.net
          </a>
        </div>
      </footer>
    </div>
  );
}